/**
 * Base error class for service layer errors
 */
export class ServiceError extends Error {
  public readonly code: string
  public readonly context?: Record<string, any>
  public readonly timestamp: Date
  public readonly recoverable: boolean
  
  constructor(
    message: string,
    code: string = 'SERVICE_ERROR',
    context?: Record<string, any>,
    recoverable: boolean = false
  ) {
    super(message)
    this.name = this.constructor.name
    this.code = code
    this.context = context
    this.timestamp = new Date()
    this.recoverable = recoverable
    
    // Maintains proper stack trace for where our error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor)
    }
  }
  
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      context: this.context,
      timestamp: this.timestamp,
      recoverable: this.recoverable,
      stack: this.stack
    }
  }
}

/**
 * Network-related errors
 */
export class NetworkError extends ServiceError {
  constructor(
    message: string,
    public readonly status?: number,
    context?: Record<string, any>
  ) {
    super(
      message,
      status ? `NETWORK_ERROR_${status}` : 'NETWORK_ERROR',
      context,
      status ? status >= 500 : true // 5xx errors are recoverable
    )
  }
}

/**
 * API-related errors
 */
export class APIError extends ServiceError {
  constructor(
    message: string,
    public readonly provider: string,
    public readonly status?: number,
    context?: Record<string, any>
  ) {
    super(
      message,
      `${provider.toUpperCase()}_API_ERROR`,
      { ...context, provider, status },
      status === 429 || status === 503 // Rate limit or service unavailable
    )
  }
}

/**
 * Validation errors
 */
export class ValidationError extends ServiceError {
  constructor(
    message: string,
    public readonly field?: string,
    context?: Record<string, any>
  ) {
    super(
      message,
      'VALIDATION_ERROR',
      { ...context, field },
      false
    )
  }
}

/**
 * Subscription/License errors
 */
export class SubscriptionError extends ServiceError {
  constructor(
    message: string,
    public readonly limitType: 'quota' | 'feature' | 'expired',
    context?: Record<string, any>
  ) {
    super(
      message,
      `SUBSCRIPTION_${limitType.toUpperCase()}`,
      context,
      false
    )
  }
}

/**
 * Rate limiting errors
 */
export class RateLimitError extends ServiceError {
  constructor(
    message: string,
    public readonly retryAfter?: number,
    context?: Record<string, any>
  ) {
    super(
      message,
      'RATE_LIMIT_ERROR',
      { ...context, retryAfter },
      true
    )
  }
}

/**
 * AI Provider specific errors
 */
export class AIProviderError extends ServiceError {
  constructor(
    message: string,
    code: string = 'AI_PROVIDER_ERROR',
    context?: Record<string, any>,
    recoverable: boolean = true
  ) {
    super(
      message,
      code,
      context,
      recoverable
    )
  }
}